import { useMemo, useState } from "react";
import MainTemplate from "../templates/MainTemplate";
import { PaginatedListTicket, Ticket } from "../../services/swagger";
import { TicketService } from "../../services/api";
import { Box, Container, TableCell } from "@mui/material";
import PriorityChip from "../atoms/PriorityChip";
import { useNavigate } from "react-router";
import TicketStatus from "../atoms/TicketStatus";
import { useGlobalContext } from "../../stores/Global";
import { useTranslation } from "react-i18next";
import DataView, { QueryParameters } from "../organisms/DataView";
import ItemCard from "../organisms/ItemCard";
import useBreakpoint from "../../hooks/useBreakpoint";
import { useQuery } from "@tanstack/react-query";
import useRole from "../../hooks/useRole";

export default function MyTicketsPage() {
  const { t } = useTranslation("Ticket");
  const breakpoint = useBreakpoint();
  const { context } = useGlobalContext();
  const navigate = useNavigate();
  const [query, setQuery] = useState<QueryParameters>();

  const checkRole = useRole();

  const getTicketReadStatus = useMemo(() => {
    if (!context.user) return (ticket: Ticket) => ticket;
    return (ticket: Ticket): Ticket & { unRead?: boolean } => {
      const username = context.user?.username || "";
      const _id = context.user?._id || "";
      let userUpdateById = ticket.usersUpdated.find((userUpdate) => {
        if (userUpdate.user) return (userUpdate.user as any) === _id;
      });
      let userUpdateByUsername = ticket.usersUpdated.find((userUpdate) => {
        if (typeof (userUpdate as any).username === "string")
          return (userUpdate as any).username === username;
      });
      const userUpdate = userUpdateById || userUpdateByUsername;
      const read = (userUpdate?.lastUpdate || 0) > ticket.lastUpdate;
      if (userUpdate) {
        return userUpdate.lastUpdate >= ticket.lastUpdate
          ? { ...ticket, unRead: !read }
          : { ...ticket, unRead: !read };
      } else return { ...ticket, unRead: !read };
    };
  }, [context.user]);

  const loadMyTickets = async (): Promise<PaginatedListTicket | null> => {
    if (!query) return null;
    const res = await TicketService.getMyTicketList({
      search: query.search,
      limit: query.limit,
      skip: query.skip,
      filters: query.filters,
      sortDirection: query.sortDirection,
      sortField: query.sortField,
    });
    if (res?.data?.data) return res.data.data;
    else return null;
  };

  const { data: tickets, isPlaceholderData: isLoading } = useQuery({
    queryFn: loadMyTickets,
    queryKey: ["my-tickets", { query }],
    refetchInterval: 1000 * 10,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    placeholderData: (previousData) => previousData,
    maxPages: 10,
  });

  const navigateToMyTicket = (ticket: Ticket) => {
    navigate(`/my-tickets/${ticket._id}`);
  };

  const handleDataTableChange = (query: QueryParameters) => {
    setQuery(query);
  };

  return (
    <MainTemplate>
      <Box
        display="flex"
        flexDirection="column"
        bgcolor="background.paper"
        height={1}
        pt={breakpoint.md ? 4 : 1}
        pb={breakpoint.md ? 4 : 1}
      >
        <Container maxWidth="xl" disableGutters sx={{ px: 1, maxHeight: 700 }}>
          <DataView
            disableFiltersOpenByDefault
            onItemClick={(item) => navigateToMyTicket(item)}
            name={t("MyTickets")}
            loading={isLoading || !tickets}
            button={{
              text: t("NewTicket"),
              onClick: () => navigate("/my-tickets/create"),
            }}
            pagination
            count={tickets?.count || 0}
            items={
              context.user
                ? tickets?.docs.map((ticket) => getTicketReadStatus(ticket)) ||
                  []
                : []
            }
            renderItem={(ticket) => (
              <ItemCard
                ticket={ticket}
                onClick={() => navigateToMyTicket(ticket)}
              />
            )}
            headers={[
              {
                field: "id",
                name: t("ID"),
                type: "number",
              },
              {
                field: "date",
                name: t("Date"),
                type: "date",
              },
              {
                field: "title",
                name: t("Title"),
                type: "other",
                renderRowCell: (row: Ticket) => (
                  <TableCell
                    sx={{
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                      maxWidth: 300,
                      textOverflow: "ellipsis",
                    }}
                  >
                    {row?.title}
                  </TableCell>
                ),
              },
              {
                field: "status",
                name: t("Status"),
                type: "other",
                renderRowCell: (row: Ticket) => {
                  return (
                    <TableCell>
                      <TicketStatus status={row.status} />
                    </TableCell>
                  );
                },
              },
              {
                field: "location",
                name: t("Location"),
                type: "other",
                renderRowCell: (row: Ticket) => (
                  <TableCell
                    sx={{
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                      maxWidth: 100,
                      textOverflow: "ellipsis",
                    }}
                  >
                    {[row.city, row.location]
                      .filter((e) => Boolean(e))
                      .join(", ")}
                  </TableCell>
                ),
              },
              {
                field: "priority",
                name: t("Priority"),
                type: "other",
                renderHeaderCell: () => (
                  <TableCell width={50} align="center">
                    {t("Priority")}
                  </TableCell>
                ),
                renderRowCell: (row: Ticket) => {
                  return (
                    <TableCell align="center">
                      <PriorityChip priority={row.priority} />
                    </TableCell>
                  );
                },
              },
            ]}
            queryFunction={
              checkRole("user")
                ? TicketService.getMyTicketListQuery
                : TicketService.getTicketListQuery
            }
            onChange={handleDataTableChange}
          />
        </Container>
      </Box>
    </MainTemplate>
  );
}
